/** Reorder array items; insertAt is the target index after the source item is removed. */
export function reorderByIndex(items, fromIndex, insertAt) {
  if (!items?.length || fromIndex == null || insertAt == null) return items ?? [];
  if (fromIndex === insertAt) return [...items];
  const next = [...items];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(insertAt, 0, removed);
  return next;
}

const getItemRects = (itemRefs) =>
  itemRefs.current
    .map((el, index) => (el ? { index, rect: el.getBoundingClientRect() } : null))
    .filter(Boolean);

/** Drop index for a wrapped horizontal tag list. */
export function resolveHorizontalDropIndex(clientX, clientY, itemRefs) {
  const entries = getItemRects(itemRefs);
  if (!entries.length) return 0;

  for (const { index, rect } of entries) {
    if (
      clientX >= rect.left
      && clientX <= rect.right
      && clientY >= rect.top
      && clientY <= rect.bottom
    ) {
      return clientX < rect.left + rect.width / 2 ? index : index + 1;
    }
  }

  let bestIndex = entries.length;
  let bestDist = Infinity;
  for (const { index, rect } of entries) {
    const midY = rect.top + rect.height / 2;
    const candidates = [
      { dropIndex: index, x: rect.left, y: midY },
      { dropIndex: index + 1, x: rect.right, y: midY },
    ];
    for (const candidate of candidates) {
      const dist = (clientX - candidate.x) ** 2 + (clientY - candidate.y) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        bestIndex = candidate.dropIndex;
      }
    }
  }
  return bestIndex;
}

/** Drop index for a vertical section list. */
export function resolveVerticalDropIndex(clientY, itemRefs) {
  const entries = getItemRects(itemRefs);
  if (!entries.length) return 0;

  for (const { index, rect } of entries) {
    if (clientY >= rect.top && clientY <= rect.bottom) {
      return clientY < rect.top + rect.height / 2 ? index : index + 1;
    }
  }

  let bestIndex = entries.length;
  let bestDist = Infinity;
  for (const { index, rect } of entries) {
    const candidates = [
      { dropIndex: index, y: rect.top },
      { dropIndex: index + 1, y: rect.bottom },
    ];
    for (const candidate of candidates) {
      const dist = Math.abs(clientY - candidate.y);
      if (dist < bestDist) {
        bestDist = dist;
        bestIndex = candidate.dropIndex;
      }
    }
  }
  return bestIndex;
}

function toLocalRect(containerEl, x, y, width, height) {
  const containerRect = containerEl.getBoundingClientRect();
  return {
    left: x - containerRect.left,
    top: y - containerRect.top,
    width,
    height,
  };
}

/** Absolute insertion bar for horizontal lists (container position: relative). */
export function getHorizontalIndicatorStyle(dropIndex, itemRefs, containerEl) {
  const items = itemRefs.current.filter(Boolean);
  if (!items.length || !containerEl) return null;

  if (dropIndex <= 0) {
    const rect = items[0].getBoundingClientRect();
    return toLocalRect(containerEl, rect.left - 5, rect.top, 4, rect.height);
  }
  if (dropIndex >= items.length) {
    const rect = items[items.length - 1].getBoundingClientRect();
    return toLocalRect(containerEl, rect.right + 3, rect.top, 4, rect.height);
  }

  const prev = items[dropIndex - 1].getBoundingClientRect();
  const next = items[dropIndex].getBoundingClientRect();
  const sameRow = Math.abs(prev.top - next.top) < 6;

  if (sameRow) {
    const x = (prev.right + next.left) / 2 - 2;
    const y = Math.min(prev.top, next.top);
    const height = Math.max(prev.height, next.height);
    return toLocalRect(containerEl, x, y, 4, height);
  }

  return toLocalRect(containerEl, next.left - 5, next.top, 4, next.height);
}

/** Absolute insertion line for vertical lists (container position: relative). */
export function getVerticalIndicatorStyle(dropIndex, itemRefs, containerEl) {
  const items = itemRefs.current.filter(Boolean);
  if (!items.length || !containerEl) return null;

  if (dropIndex <= 0) {
    const rect = items[0].getBoundingClientRect();
    return toLocalRect(containerEl, rect.left, rect.top - 5, rect.width, 4);
  }
  if (dropIndex >= items.length) {
    const rect = items[items.length - 1].getBoundingClientRect();
    return toLocalRect(containerEl, rect.left, rect.bottom + 3, rect.width, 4);
  }

  const prev = items[dropIndex - 1].getBoundingClientRect();
  const next = items[dropIndex].getBoundingClientRect();
  const y = (prev.bottom + next.top) / 2 - 2;
  const x = Math.min(prev.left, next.left);
  const width = Math.max(prev.right, next.right) - x;
  return toLocalRect(containerEl, x, y, width, 4);
}
